import { FC, useContext, useEffect, useState } from 'react'

import { FileToGenerate } from '__generated__/atlas-types'

import { useRenderToasters } from 'hooks/useRenderToasters'

import { ImportOrdersModalIds } from 'modals/OrderList/OrderImportModal/orderImportModal.ids'

import { ImportOrdersContext } from '../../ImportOrders.context'
import SummaryTable from './SummaryTable/SummaryTable'
import { useGenerateSummaryFile } from './ThirdStepVerticalHorizontal.hooks'
import {
  ButtonLink,
  Container,
  DownloadingMessage,
  StyledCheck,
  StyledErrorTitle,
  StyledErrors,
  SummaryWrapper,
} from './ThirdStepVerticalHorizontal.styles'
import { ErrorsData, OrdersAdapted } from './ThirdStepVerticalHorizontal.types'
import {
  downloadURL,
  getErrorsData,
  getSummaryData,
} from './ThirdStepVerticalHorizontal.utils'

const ThirdStep: FC = () => {
  const {
    retailer,
    setImportEnabled,
    validatedOrders,
    setErrorMsg,
    setModalTitle,
  } = useContext(ImportOrdersContext)

  const [downloadSummaryEnabled, setDownloadSummaryEnabled] =
    useState<boolean>(true)
  const [downloadErrorsEnabled, setDownloadErrorsEnabled] =
    useState<boolean>(true)
  const [ignoreErrors, setIgnoreErrors] = useState<boolean>(false)

  useEffect(() => setModalTitle('Import Orders'), [setModalTitle])

  const generateSummaryFile = useGenerateSummaryFile()

  const { renderErrorToast } = useRenderToasters()

  // retailer option label appends city and/or state if location is found
  const customerName = !retailer?.label
    ? ''
    : retailer.label.includes(' - ') // city separator
      ? retailer?.label?.split(' - ')[0]
      : retailer.label.includes(', ') // state separator
        ? retailer?.label?.split(', ')[0]
        : retailer.label
  const summaryData: OrdersAdapted = getSummaryData(
    validatedOrders,
    customerName,
  )
  const errorsData: ErrorsData = getErrorsData(validatedOrders)
  const ordersNumber: number = summaryData?.aggregated?.skus || 0

  useEffect(() => {
    if (ordersNumber === 0) {
      setErrorMsg('No orders to import, please review error details')
      setImportEnabled(false)
    } else if (errorsData.length > 0 && !ignoreErrors) {
      setErrorMsg('Fix or ignore errors to complete import')
      setImportEnabled(false)
    } else {
      setErrorMsg('')
      setImportEnabled(true)
    }

    return () => {
      setErrorMsg('')
      setImportEnabled(true)
    }
  }, [ordersNumber, errorsData, ignoreErrors])

  const downloadDetails = (fileType: FileToGenerate): void => {
    generateSummaryFile(validatedOrders?.validatedOrdersIdentifier, fileType)
      // @ts-expect-error
      .then((response) => {
        if (!response?.errors && response?.data) {
          downloadURL(response?.data?.generateSummaryFile?.url)
        } else {
          renderErrorToast({ description: 'Error downloading the file' })
        }
      })
      .catch((error: string) => {
        renderErrorToast({
          description: `Error downloading the file: ${error}`,
        })
      })
      .finally(() => {
        setDownloadSummaryEnabled(true)
        setDownloadErrorsEnabled(true)
      })
  }

  const downloadOrderDetails = (): void => {
    setDownloadSummaryEnabled(false)
    downloadDetails(FileToGenerate.ORDERS_DETAILS)
  }

  const downloadErrorDetails = (): void => {
    setDownloadErrorsEnabled(false)
    downloadDetails(FileToGenerate.ORDER_ERRORS)
  }

  return (
    <SummaryWrapper
      id={ImportOrdersModalIds.ImportSummaryContainer}
      data-testid={ImportOrdersModalIds.ImportSummaryContainer}
    >
      <Container>
        {/* @ts-expect-error */}
        <SummaryTable summaryTableData={summaryData} />
        {downloadSummaryEnabled ? (
          <ButtonLink
            id={ImportOrdersModalIds.OrderDetailsLinkId}
            type="button"
            onClick={downloadOrderDetails}
          >
            Download Order Details
          </ButtonLink>
        ) : (
          <DownloadingMessage>
            Generating and downloading file...
          </DownloadingMessage>
        )}
      </Container>

      {errorsData.length > 0 && (
        <>
          <StyledErrorTitle id={ImportOrdersModalIds.ErrorsTitleId}>
            Errors
          </StyledErrorTitle>
          <Container>
            {errorsData.map((error, index) => (
              <StyledErrors key={`errorItem${index}`}>{error}</StyledErrors>
            ))}
            <StyledCheck
              id={ImportOrdersModalIds.IgnoreErrorsCheckboxId}
              label="Ignore Errors (Items with an error will not be imported)"
              onChange={() => setIgnoreErrors(!ignoreErrors)}
            />
            {downloadErrorsEnabled ? (
              <ButtonLink
                id={ImportOrdersModalIds.DownloadErrorDetailsLinkId}
                type="button"
                onClick={downloadErrorDetails}
              >
                Download Error Details
              </ButtonLink>
            ) : (
              <DownloadingMessage>
                Generating and downloading file...
              </DownloadingMessage>
            )}
          </Container>
        </>
      )}
    </SummaryWrapper>
  )
}

export default ThirdStep
